// src/data/html.js
export const htmlNotes = [
  {
    id: 201,
    topic: "html",
    seriesKey: "html",
    title: "Introduction to HTML",
    description:
      "Understanding what HTML is, its creator, and the essential tags required to structure any web page.",
    content: `
##  What is HTML?
**HTML** stands for **HyperText Markup Language**.
* It is the standard markup language used to create web pages.
* It describes the structure of a web page using a system of elements and tags.
### History & Developer Info
 **Creator:** *Tim Berners-Lee*.
 **Year of Conception:** *1991* (first public draft).
 **Function:** HTML provides the *content* and the *skeleton* of the website; it does **not** define the style (that's CSS) or the interactivity (that's JavaScript).
##  Basic HTML Document Structure
Every well-formed HTML document must include these essential tags:
\`\`\`html
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>My Web Page Title</title>
</head>
<body>
    </body>
</html>
\`\`\`
### Key Components
* \`<!DOCTYPE html>\`: Must be the very first line. It tells the browser which HTML standard to expect (HTML5).
* \`<html>\`: The root element of the entire page.
* \`<head>\`: Contains meta-information about the HTML document (e.g., character set, title, links to CSS/JS). This content is not displayed on the page.
* \`<body>\`: Contains all the visible content of the page (text, images, links, etc.).
        `,
    tags: ["HTML", "Basics", "Structure", "Tim Berners-Lee"],
    date: "2024-11-12",
  },
  {
    id: 202,
    topic: "html",
    seriesKey: "html",
    title: "HTML Fundamentals: Elements, Tags, and Attributes",
    description:
      "Defining the core building blocks of HTML: elements, their tags, and the attributes used to configure them.",
    content: `
##  The Building Blocks of HTML
### 1. Elements and Tags
* An HTML Element is a component of an HTML document (e.g., a heading, a paragraph, an image).
* An HTML element is usually represented by a pair of tags: an opening tag and a closing tag.
    \`\`\`html
    <p>This is an element's content.</p>
    ^ Opening Tag          ^ Closing Tag
    \`\`\`
* Self-Closing Tags: Some elements do not have content and only require a single tag (e.g., \`<br>\`, \`<img>\`, \`<input>\`).
### 2. Attributes
* Attributes provide additional information about an element.
* They are always specified in the opening tag and typically come in name="value" pairs.
\`\`\`html
<a href="https://example.com" target="_blank">External Link</a>
\`\`\`
### 3. Text Formatting Tags
These tags define how text should be displayed, with emphasis on semantic meaning.
| Tag | Display | Semantic Meaning |
| :--- | :--- | :--- |
| \`<h1>\` to \`<h6>\` | Headings (size 1 is largest) | Defines the structural hierarchy of content. |
| \`<p>\` | Paragraph | Defines a block of text. |
| \`<strong>\` | Bold | Indicates strong importance (semantic). |
| \`<em>\` | Italic | Indicates emphasized text (semantic). |
| \`<br>\` | Line Break | Forces a line break (self-closing). |
| \`<hr>\` | Horizontal Rule | Defines a thematic break (self-closing). |
        `,
    tags: ["HTML", "Tags", "Attributes", "Elements", "Text Formatting"],
    date: "2024-11-12",
  },
  {
    id: 203,
    topic: "html",
    seriesKey: "html",
    title: "Semantic HTML: Structure and Meaning",
    description:
      "Using modern HTML5 structural tags to give meaning to your content, improving SEO and accessibility.",
    content: `
##  Semantic Tags
Semantic tags clearly describe their meaning to both the browser and the developer. This is crucial for **SEO** (Search Engine Optimization) and **Accessibility**.
| Tag | Purpose | Description |
| :--- | :--- | :--- |
| \`<header>\` | Introduction | Contains introductory content, often including the title, logo, and navigation. |
| \`<nav>\` | Navigation | Contains navigation links (menus). |
| \`<main>\` | Primary Content | Contains the dominant content unique to the document. There should be only one per page. |
| \`<section>\` | Thematic Grouping | Defines a section in a document (e.g., 'Introduction', 'About Us'). |
| \`<article>\` | Independent Content | Defines independent, self-contained content (e.g., a blog post, news story, forum comment). |
| \`<aside>\` | Sidebar Content | Content related to the main content but separate from it (e.g., advertisements, related links). |
| \`<footer>\` | Conclusion | Contains authorship information, copyright data, or contact details. |
### Semantic Example
\`\`\`html
<body>
    <header>
        <h1>Website Logo</h1>
        <nav>
            <a href="/">Home</a>
        </nav>
    </header>
    <main>
        <section>
            <h2>Latest Posts</h2>
            <article>
                <h3>Post Title</h3>
                <p>Content of the post...</p>
            </article>
        </section>
    </main>
    <footer>
        <p>&copy; 2024</p>
    </footer>
</body>
\`\`\`
        `,
    tags: ["HTML", "Semantic", "HTML5", "SEO", "Accessibility"],
    date: "2024-11-13",
  },
  {
    id: 204,
    topic: "html",
    seriesKey: "html",
    title: "Links and Navigation with Anchor Tags",
    description:
      "Creating hyperlinks to connect documents internally, externally, and within the same page.",
    content: `
## Links and Anchor Tags
The **anchor tag (\`a\`)** is used to define a hyperlink, which links one web page to another.
### Key Attributes
  **1.\`href\` (Hypertext Reference):** Specifies the destination URL for the link. This is the **most important** attribute.
  **2.\`target\`:** Specifies where to open the linked document.
    * \`_self\` (Default): Opens in the same window/tab.
    * \`_blank\`: Opens in a new window/tab.
### Internal vs. External Links
 **External Links:** Full URL to another website.
    \`\`\`html
    <a href="https://google.com" target="_blank">Go to Google</a>
    \`\`\`
**Internal Links:** Relative path to another page within the same website.
    \`\`\`html
    <a href="/about/team.html">Our Team</a>
    \`\`\`
### Anchor Links (Page Navigation)
Used to link to a specific section *within* the same page. This requires setting the **\`id\`** attribute on the target element.
  **1.Set the target \`id\`:**
    \`\`\`html
    <section id="contact-info">
        <h2>Contact</h2>
        </section>
    \`\`\`
  **2.Create the link using \`#\`:**
    \`\`\`html
    <a href="#contact-info">Jump to Contact</a>
    \`\`\`
### Navigation Menus
Navigation links are typically wrapped within a **\`nav\`** tag and often use an unordered list (\`ul\`) for structure.
        `,
    tags: ["HTML", "Links", "Navigation", "Anchor", "href", "target"],
    date: "2024-11-13",
  },
  {
    id: 205,
    topic: "html",
    seriesKey: "html",
    title: "Images and Multimedia Basics",
    description:
      "Embedding images and media using the <img>, <audio>, and <video> tags.",
    content: `
##  Images (\`<img>\`)
The \`img\` tag is used to embed an image into an HTML page. It is a self-closing tag.
### Essential Attributes
1.  **\`src\` (Source):** Specifies the path (URL) to the image file.
2.  **\`alt\` (Alternate Text):** Provides a text description of the image. **Crucial for accessibility** and SEO; displayed if the image fails to load.
\`\`\`html
<img src="/assets/logo.png" alt="Company logo - a stylized letter C" width="150" height="150">
\`\`\`
### Other Attributes
* \`width\` and \`height\`: Defines the image size (in pixels). Best practice is to include these to prevent layout shifting (CLS).
* \`title\`: Provides extra information, displayed as a tooltip when the mouse hovers over the image (less common now).
##  Audio and Video (\`<audio>\`, \`<video>\`)
The \`audio\` and \`video\` tags allow embedding media without relying on third-party plugins.
\`\`\`html
<video src="movie.mp4" width="640" height="360" controls autoplay muted>
    Your browser does not support the video tag.
</video>
\`\`\`
### Common Attributes
* \`controls\`: Displays the default playback controls (play/pause, volume, etc.).
* \`autoplay\`: Starts playing automatically (often blocked by browsers unless \`muted\` is also present).
* \`loop\`: Starts the media over again when it finishes.
* \`muted\`: Silences the audio output.
        `,
    tags: ["HTML", "Images", "Multimedia", "img", "alt", "video", "audio"],
    date: "2024-11-14",
  },
  {
    id: 206,
    topic: "html",
    seriesKey: "html",
    title: "Lists: Ordered, Unordered, and Description Lists",
    description:
      "Structuring content using different types of lists for sequential, bulleted, or term-based groupings.",
    content: `
## Lists in HTML
Lists are fundamental for organizing related items on a page.
### 1. Unordered Lists (\`<ul>\`)
Used for a group of related items where the **order does not matter** (displayed with bullet points by default).
\`\`\`html
<ul>
    <li>Coffee</li>
    <li>Tea</li>
    <li>Milk</li>
</ul>
\`\`\`
### 2. Ordered Lists (\`<ol>\`)
Used for a group of related items where the **order is important** (displayed with numbers or letters by default).
\`\`\`html
<ol>
    <li>First step</li>
    <li>Second step</li>
    <li>Third step</li>
</ol>
\`\`\`
### Nested Lists (\`<li>\`)
You can place one list inside another (e.g., inside an \`li\`) to create a hierarchical structure.
\`\`\`html
<ul>
    <li>Web Technologies
        <ol>
            <li>HTML</li>
            <li>CSS</li>
        </ol>
    </li>
    <li>Databases</li>
</ul>
\`\`\`
### 3. Description Lists (\`<dl>\`)
Used to structure data where terms and their definitions/descriptions are grouped.
* \`<dl>\`: Defines the description list itself.
* \`<dt>\`: Defines the term (Data Term).
* \`<dd>\`: Defines the description or definition of the term (Data Definition).
\`\`\`html
<dl>
    <dt>HTML</dt>
    <dd>HyperText Markup Language.</dd>
    <dt>CSS</dt>
    <dd>Cascading Style Sheets.</dd>
</dl>
\`\`\`
        `,
    tags: ["HTML", "Lists", "ul", "ol", "dl"],
    date: "2024-11-14",
  },
  {
    id: 207,
    topic: "html",
    seriesKey: "html",
    title: "Tables: Structure, Headers, and Spanning Cells",
    description:
      "Creating tabular data structures using the <table> element, including row/column spanning.",
    content: `
## Tables in HTML
Tables are used for displaying **tabular data**, not for page layout.
### Basic Table Structure
| Tag | Purpose |
| :--- | :--- |
| \`<table>\` | Container for all table content. |
| \`<tr>\` | Defines a Table Row. |
| \`<th>\` | Defines a Table Header cell (bold and centered by default). |
| \`<td>\` | Defines a standard Table Data cell. |
| \`<caption>\` | Defines a title/caption for the table. |
\`\`\`html
<table>
    <caption>Monthly Sales Report</caption>
    <tr>
        <th>Month</th>
        <th>Revenue</th>
    </tr>
    <tr>
        <td>January</td>
        <td>$5,000</td>
    </tr>
</table>
\`\`\`
### Cell Spanning (\`colspan\` & \`rowspan\`)
Used to make a cell span across multiple columns or rows.
* \`colspan="N"\`: Makes the cell span across N columns.
    \`\`\`html
    <th colspan="2">Q1 Total</th> 
    \`\`\`
* \`rowspan="N"\`: Makes the cell span across N rows.
### Table Styling Basics
While modern styling should be handled by CSS, older, basic attributes like \`border\`, \`cellpadding\`, and \`align\` were traditionally used on the table and cell tags. **These are generally deprecated in favor of CSS.**
        `,
    tags: ["HTML", "Tables", "Colspan", "Rowspan", "thead", "tbody"],
    date: "2024-11-15",
  },
  {
    id: 208,
    topic: "html",
    seriesKey: "html",
    title: "Forms and Inputs: Collecting User Data",
    description:
      "Mastering the <form> element and various input types for gathering information from a user.",
    content: `
##  Forms (\`<form>\`)
The \`form\` element is the container for all input fields and defines how data is submitted.
### Key \`<form>\` Attributes
* \`action\`: The URL where the form data will be sent upon submission (usually a server-side script).
* \`method\`: The HTTP method to use for sending the data.
    * \`GET\`: Appends form data to the URL (visible, limited data). Best for searches.
    * \`POST\`: Sends form data in the body of the HTTP request (secure, large data). Best for submissions.
### Form Fields and Labels
* \`<label>\`: Crucial for accessibility. It explicitly links text to a specific input field using the \`for\` attribute matching the input's \`id\`.
\`\`\`html
<form action="/submit-data" method="post">
    <label for="username">Username:</label>
    <input type="text" id="username" name="username">
    <input type="submit" value="Sign Up">
</form>
\`\`\`
##  Input Types (\`<input>\`)
The \`input\` tag is the most flexible form element, controlled by the \`type\` attribute.

| Type | Purpose |
| :--- | :--- |
| \`text\` | Default single-line text input. |
| \`password\` | Text input where characters are masked. |
| \`email\` | Input expecting an email format (basic browser validation). |
| \`number\` | Input accepting only numeric values. |
| \`date\` | A date picker widget. |
| \`checkbox\` | Allows selection of zero or more options. |
| \`radio\`| Allows selection of exactly one option from a group (must share the same \`name\` attribute). |
| \`file\` | Allows the user to select one or more files to upload. |
| \`submit\` | A button that submits the form data to the server. |
        `,
    tags: ["HTML", "Forms", "Input", "Label", "Action", "Method"],
    date: "2024-11-15",
  },
  {
    id: 209,
    topic: "html",
    seriesKey: "html",
    title: "Global Attributes and Data Attributes",
    description:
      "Attributes available on all HTML elements for unique identification, styling, and storing custom data.",
    content: `
##  Global Attributes
**Global attributes** are attributes that can be used on **every single HTML element**.
| Attribute | Purpose |
| :--- | :--- |
| \`id\` | Specifies a unique identifier for an element (only one element should have a given ID). Used by CSS, JavaScript, and anchor links. |
| \`class\` | Specifies one or more class names for an element. Used primarily by CSS and JavaScript to group elements for styling/manipulation. |
| \`title\` | Provides extra information about an element, usually displayed as a tooltip. |
| \`style\` | Specifies inline CSS styles (generally discouraged in favor of external CSS). |
| \`lang\` | Specifies the language of the element's content (e.g., \`lang="en"\`). |
| \`dir\` | Specifies the text direction (e.g., \`dir="rtl"\` for Right-To-Left languages). |
### Accessing Elements
 **Using \`id\`:** In CSS (\`#myID\`) or JavaScript (\`getElementById('myID')\`).
 **Using \`class\`:** In CSS (\`.myClass\`) or JavaScript (\`getElementsByClassName('myClass')\` / \`querySelector('.myClass')\`).
##  Data Attributes (\`data-*\`)
HTML5 introduced the ability to store **custom data** privately in standard elements using attributes starting with **\`data-\`**.
 **Purpose:** To store extra information about an element that is intended for use by JavaScript (e.g., storing a user ID, product settings, or status).
\`\`\`html
<div class="user-profile" data-user-id="12345" data-status="active">
    Profile Card
</div>
\`\`\`
 **Access in JavaScript:** You access these values using the element's \`dataset\` property (e.g., \`element.dataset.userId\` returns \`"12345"\`).
        `,
    tags: ["HTML", "Attributes", "Global Attributes", "data-*", "ID", "Class"],
    date: "2024-11-16",
  },
  {
    id: 210,
    topic: "html",
    seriesKey: "html",
    title: "HTML5 Structural and Grouping Tags",
    description:
      "Deepening the understanding of modern semantic tags for structuring larger, more complex documents.",
    content: `
##  Advanced Structural Tags (HTML5)
These tags define the structure and intent of larger blocks of content. 
### 1. \`<main>\`
* Contains the unique, primary content of the document.
* Rule: There must be only one \`<main>\` element per document. It should not be a descendant of \`<article>\`, \`<aside>\`, \`<footer>\`, \`<header>\`, or \`<nav>\`.
### 2. \`<figure>\` 
 It Represents self-contained content, usually an image, illustration, diagram, code snippet, etc., that is referenced from the main flow of the document.
 ### 3. \`<figcaption>\`
 It Provides a caption for the \`figure\` element.
\`\`\`html
<figure>
    <img src="chart.png" alt="Sales growth chart">
    <figcaption>Figure 1: Quarterly Sales Performance.</figcaption>
</figure>
\`\`\`
### 3. \`<section>\` vs. \`<article>\`
This distinction is often confusing:
* \`<article>\`: Should make sense even if syndicated or read outside the context of the rest of the page (e.g., a standalone blog post).
* \`<section>\`: A generic thematic grouping of content. It only makes sense as part of the page's overall structure (e.g., an 'Introduction' section on a homepage).
        `,
    tags: [
      "HTML",
      "Structural",
      "HTML5",
      "figure",
      "main",
      "article",
      "section",
    ],
    date: "2024-11-16",
  },
  {
    id: 211,
    topic: "html",
    seriesKey: "html",
    title: "Multimedia Embedding and iFrames",
    description:
      "Embedding content from external sources (like YouTube or Maps) and exploring advanced media attributes.",
    content: `
##  Embedding External Content (\`<iframe>\`)
The \`iframe\` tag is used to embed another HTML document (from a different domain or the same) within the current document.
 **Use Cases:** Embedding YouTube videos, Google Maps, or external advertisements.
 **Security:** Be cautious about embedding content from untrusted sources, as it can pose security risks.
### Key Attributes
 **\`src\`**: The URL of the document to embed.
 **\`width\` & \`height\`**: Defines the dimensions of the inline frame.
 **\`frameborder\`**: (Deprecated, use CSS) Used to remove the border around the frame.
\`\`\`html
<iframe 
    width="560" 
    height="315" 
    src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
    title="YouTube video player" 
    allowfullscreen>
</iframe>
\`\`\`
##  Advanced \`<audio>\` & \`<video>\` Attributes
The media tags have attributes that control how content behaves.
 **\`controls\`**: Essential for user interaction. Displays the player interface.
 **\`autoplay\`**: Attempts to auto-play the media upon loading. **(Often requires \`muted\` to work in modern browsers.)**
 **\`loop\`**: Makes the media automatically restart after it finishes.
 **\`muted\`**: Silences the media's audio by default.
 **\`preload\`**: Hints to the browser about whether the file should be loaded immediately. (\`none\`, \`metadata\`, \`auto\`).
        `,
    tags: ["HTML", "Multimedia", "iframe", "video", "Embedding", "autoplay"],
    date: "2024-11-17",
  },
  {
    id: 212,
    topic: "html",
    seriesKey: "html",
    title: "Advanced Form Elements and Validation",
    description:
      "Grouping form fields, enforcing data constraints, and providing browser-based validation.",
    content: `
##  Form Field Grouping
* \`<fieldset>\`: Used to group related elements in a form (e.g., "Personal Info," "Billing Address").
* \`<legend>\`: Provides a caption/title for the contents of the \`<fieldset>\`.
\`\`\`html
<fieldset>
    <legend>Contact Information</legend>
    <label for="phone">Phone:</label>
    <input type="tel" id="phone">
</fieldset>
\`\`\`
##  Advanced Input Attributes
HTML5 provides built-in validation attributes that trigger browser-level error messages.
| Attribute | Purpose |
| :--- | :--- |
| \`required\` | The field must be filled out before submission. |
| \`placeholder\` | Provides a hint to the user about what to enter (fades when typing starts). |
| \`maxlength="N"\` | Specifies the maximum number of characters allowed. |
| \`readonly\` | The user cannot modify the value (but it is still submitted). |
| \`disabled\` | The field is unusable and its value is not submitted. |
| \`pattern="regex"\` | Specifies a regular expression the input value must match for validation. |
### Data Lists (\`<datalist>\`)
Provides a list of suggested options for an  *\`input\`* field, while still allowing the user to type something else.
\`\`\`html
<input list="browsers" name="browser">
<datalist id="browsers">
    <option value="Chrome">
    <option value="Firefox">
</datalist>
\`\`\`
        `,
    tags: ["HTML", "Forms", "Validation", "required", "fieldset", "datalist"],
    date: "2024-11-17",
  },
  {
    id: 213,
    topic: "html",
    seriesKey: "html",
    title: "Meta Tags and SEO Fundamentals",
    description:
      "Using the <meta> tag to provide crucial information to browsers and search engines about the page content.",
    content: `
##  Meta Tags and SEO Basics
**Meta tags** are placed inside the *\`head\`* and contain metadata (data about data) that is used by browsers, search engines, and social media platforms.
### 1. Essential Meta Tags
* Character Set: Must be the first element in \`<head>\`.
    \`\`\`html
    <meta charset="UTF-8">
    \`\`\`
* Viewport (RWD): Critical for Responsive Web Design (RWD). Tells the browser to set the page width to the device width.
    \`\`\`html
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    \`\`\`
### 2. SEO-Related Meta Tags
* \`<title>\`: The most important SEO tag. The text appears on the browser tab and as the main link heading in search results.
    \`\`\`html
    <title>HTML Forms Guide | Web Dev Basics</title>
    \`\`\`
* \`description\`: A short, accurate summary of the page content. This is often displayed under the title in search engine results.
    \`\`\`html
    <meta name="description" content="A comprehensive guide to all HTML form elements and validation attributes.">
    \`\`\`
* \`keywords\`: (Largely ignored by modern search engines, but included for completeness). A comma-separated list of relevant terms.
    \`\`\`html
    <meta name="keywords" content="HTML, forms, input, validation">
    \`\`\`
        `,
    tags: ["HTML", "Meta", "SEO", "Viewport", "Title"],
    date: "2024-11-18",
  },
  {
    id: 214,
    topic: "html",
    seriesKey: "html",
    title: "Linking CSS and JavaScript",
    description:
      "Connecting the three core web technologies and best practices for where to place script tags.",
    content: `
##  Linking External Files
### 1. Linking CSS (\`<link>\`)
CSS is primarily linked using the self-closing *\`link\`* tag, which is placed inside the *\`head\`* element.
\`\`\`html
<link rel="stylesheet" href="/css/styles.css">

<style>
    body { background-color: lightgray; }
</style>
\`\`\`
* \`rel="stylesheet"\`: Specifies the relationship between the HTML and the linked file.
* \`href\`: Specifies the path to the CSS file.
### 2. Linking JavaScript (\`<script>\`)
The *\`script\`* tag is used to embed or reference executable JavaScript code.
\`\`\`html
<script src="/js/app.js"></script> 

<script>
    console.log("Hello from inline script!");
</script>
\`\`\`
### Placement of \`<script>\`
* Old Practice (In \`<head>\`): Scripts here block rendering. The page won't display until the script is downloaded and executed.
* Best Practice (End of \`<body>\`): Placing scripts just before the closing \`</body>\` tag ensures that the entire DOM is loaded before the script attempts to interact with it, and it prevents script loading from blocking the visible page rendering.
* Modern Attributes for \`<head>\`:
    * \`defer\`: Loads the script asynchronously and executes it only after the HTML document is fully parsed. (Recommended for most scripts.)
    * \`async\`: Loads the script asynchronously and executes it as soon as it's downloaded, potentially before the HTML is fully parsed. (Good for independent scripts like analytics.)
        `,
    tags: ["HTML", "Linking", "script", "link", "defer", "async"],
    date: "2024-11-18",
  },
  {
    id: 215,
    topic: "html",
    seriesKey: "html",
    title: "HTML Best Practices: Semantics, Nesting, and Accessibility",
    description:
      "A summary of rules and guidelines for writing clean, accessible, and high-quality HTML code.",
    content: `
##  HTML Best Practices
### 1. Prioritize Semantic HTML
* Use the Right Tag for the Job: Avoid using non-semantic tags like \`<div>\` and \`<span>\` when a semantic tag (like \`<header>\`, \`<nav>\`, \`<button>\`) exists.
* Benefits: Improves SEO (search engines understand content better) and Accessibility (screen readers can navigate the page structure).
### 2. Proper Nesting and Validation
* Closure: Always close your tags (unless self-closing).
* Nesting: Ensure elements are nested correctly (e.g., a \`<div>\` cannot be inside a \`<p>\`). Incorrect nesting leads to unpredictable rendering and accessibility issues.
* W3C Validator: Use online tools to validate your HTML to catch syntax errors and ensure compliance.
### 3. Styling and Separation of Concerns
* Avoid Inline Styling (\`style\` attribute): Inline styles are hard to manage and override. Keep all styling definitions in external CSS files.
    * Exception: Necessary for some component libraries or specific JavaScript-driven dynamic styles.
### 4. Accessibility (A11Y)
Accessibility ensures that people with disabilities can perceive, understand, navigate, and interact with the web.
* \`alt\` Text: Every image must have meaningful \`alt\` text (or \`alt=""\` if purely decorative).
* Labels: Every form input must be explicitly linked to a \`<label>\`.
* Headings: Use the \`<h1>\` through \`<h6>\` hierarchy correctly (e.g., only one \`<h1>\` per page) to define structure.
* ARIA Attributes: Advanced attributes (e.g., \`aria-label\`, \`aria-expanded\`) provide additional information to assistive technologies when standard HTML tags are insufficient (e.g., custom widgets).
        `,
    tags: ["HTML", "Best Practices", "Accessibility", "SEO", "Semantics"],
    date: "2024-11-19",
  },
];
